
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Room } from "@/types/chat";
import { ChatView } from "@/components/chat/ChatView";
import { RoomsList } from "@/components/chat/RoomsList";
import { useRooms } from "@/components/chat/hooks/useRooms";
import { useChat } from "@/components/chat/hooks/useChat";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

interface Follower {
  id: string;
  username: string;
  avatar_url: string | null;
}

const Inbox = () => {
  const { user } = useAuth();
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const { rooms, isLoading: isLoadingRooms, refreshRooms } = useRooms();
  const { messages, isLoading: isLoadingMessages, sendMessage } = useChat(selectedRoom?.id || null);
  const [newMessage, setNewMessage] = useState("");
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [isLoadingFollowers, setIsLoadingFollowers] = useState(false);

  useEffect(() => {
    if (isNewChatOpen && user) {
      fetchFollowers();
    }
  }, [isNewChatOpen, user]);

  // Force refreshRooms on initial load
  useEffect(() => {
    if (user && !isLoadingRooms) {
      refreshRooms();
    }
  }, [user, isLoadingRooms, refreshRooms]);

  const fetchFollowers = async () => {
    if (!user) return;
    
    try {
      setIsLoadingFollowers(true);
      
      // Get users following the current user
      const { data: followersData, error: followersError } = await supabase
        .from('user_follows')
        .select(`
          follower_id,
          profiles!follower_id(
            id,
            username,
            avatar_url
          )
        `)
        .eq('following_id', user.id);
      
      if (followersError) {
        console.error('Error fetching followers:', followersError);
        return;
      }
      
      // Transform data to more usable format
      const formattedFollowers = followersData
        .filter(item => item.profiles) // Filter out any entries with missing profile info
        .map(item => {
          const profile = Array.isArray(item.profiles) ? item.profiles[0] : item.profiles;
          return {
            id: profile.id,
            username: profile.username,
            avatar_url: profile.avatar_url
          };
        });
      
      setFollowers(formattedFollowers);
    } catch (error) {
      console.error('Unexpected error fetching followers:', error);
    } finally {
      setIsLoadingFollowers(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Please sign in to view your messages</p>
      </div>
    );
  }

  if (isLoadingRooms) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-flux-cyan"></div>
      </div>
    );
  }

  const handleSendMessage = async () => {
    if (!selectedRoom || !newMessage.trim()) return;
    await sendMessage(newMessage);
    setNewMessage("");
  };

  const handleCreateChat = async (selectedUserId?: string, selectedUsername?: string) => {
    const userIdToChat = selectedUserId || null;
    const usernameToChat = selectedUsername || usernameInput.trim();
    
    if (!userIdToChat && !usernameToChat) {
      toast.error("Please enter a username");
      return;
    }

    try {
      setIsCreatingChat(true);

      let otherUserId = userIdToChat;
      let otherUsername = selectedUsername || usernameToChat;

      // If we don't have the user ID yet, find the user by username
      if (!otherUserId) {
        const { data: otherUser, error: userError } = await supabase
          .from('profiles')
          .select('id, username')
          .eq('username', usernameToChat)
          .single();

        if (userError || !otherUser) {
          toast.error("User not found. Please check the username and try again.");
          return;
        }

        otherUserId = otherUser.id;
        otherUsername = otherUser.username;
      }

      if (otherUserId === user.id) {
        toast.error("You cannot create a chat with yourself.");
        return;
      }

      // Check if a chat already exists between these users
      const { data: existingParticipations1, error: existingError1 } = await supabase
        .from('chat_room_participants')
        .select('room_id')
        .eq('user_id', user.id);

      if (existingError1) {
        console.error("Error checking existing chats:", existingError1);
        toast.error("Failed to check for existing chats.");
        return;
      }

      if (existingParticipations1?.length) {
        const roomIds = existingParticipations1.map(p => p.room_id);
        
        const { data: existingParticipations2, error: existingError2 } = await supabase
          .from('chat_room_participants')
          .select('room_id')
          .eq('user_id', otherUserId)
          .in('room_id', roomIds);

        if (existingError2) {
          console.error("Error checking existing chats:", existingError2);
          toast.error("Failed to check for existing chats.");
          return;
        }

        // If a chat already exists, find and open it
        if (existingParticipations2?.length) {
          // Get the room info
          const { data: existingRoom, error: roomError } = await supabase
            .from('chat_rooms')
            .select('*')
            .eq('id', existingParticipations2[0].room_id)
            .single();

          if (!roomError && existingRoom && !existingRoom.is_group) {
            // Refresh rooms first to make sure our local state is up to date
            await refreshRooms();
            
            // Find the room in our local state
            const existingRoomInState = rooms?.find(r => r.id === existingRoom.id);
            if (existingRoomInState) {
              setIsNewChatOpen(false);
              setUsernameInput("");
              setSelectedRoom(existingRoomInState);
              toast.info("Chat already exists. Opening the conversation.");
              return;
            }
          }
        }
      }

      // Create new chat room
      const { data: newRoom, error: roomError } = await supabase
        .from('chat_rooms')
        .insert({
          created_by: user.id,
          is_group: false
        })
        .select()
        .single();

      if (roomError) {
        console.error("Error creating room:", roomError);
        toast.error("Failed to create chat room.");
        return;
      }

      // Add both users as participants
      const { error: participantsError } = await supabase
        .from('chat_room_participants')
        .insert([
          { room_id: newRoom.id, user_id: user.id },
          { room_id: newRoom.id, user_id: otherUserId }
        ]);

      if (participantsError) {
        console.error("Error adding participants:", participantsError);
        toast.error("Failed to add participants to chat room.");
        return;
      }

      // Close the dialog and clear the input
      setIsNewChatOpen(false);
      setUsernameInput("");
      
      // Force refresh the rooms list to get the new room
      await refreshRooms();
      
      // Create a temporary room object to show immediately
      const tempRoom: Room = {
        ...newRoom,
        participants: [
          {
            user_id: otherUserId,
            joined_at: new Date().toISOString(),
            profiles: {
              username: otherUsername,
              avatar_url: null
            }
          }
        ],
        is_group: false
      };
      
      // Set the selected room to the new room
      setSelectedRoom(tempRoom);
      toast.success(`Chat with ${otherUsername} created!`);
      
    } catch (error) {
      console.error("Unexpected error creating chat:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setIsCreatingChat(false);
    }
  };

  const filteredFollowers = usernameInput
    ? followers.filter(follower => 
        follower.username.toLowerCase().includes(usernameInput.toLowerCase()))
    : followers;

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      <RoomsList
        rooms={rooms}
        selectedRoom={selectedRoom}
        onSelectRoom={setSelectedRoom}
        onCreateChat={() => setIsNewChatOpen(true)}
        className="border-flux-cyan/20"
      />
      {selectedRoom ? (
        <ChatView
          room={selectedRoom}
          messages={messages}
          newMessage={newMessage}
          onMessageChange={setNewMessage}
          onSendMessage={handleSendMessage}
          onStartVideoCall={() => {}}
          onStartVoiceCall={() => {}}
          isInCall={false}
          currentUserId={user.id}
          onBack={() => setSelectedRoom(null)}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center bg-background">
          <div className="text-center max-w-md p-6">
            <h2 className="text-2xl font-semibold mb-3">Your messages</h2>
            <p className="text-muted-foreground mb-4">
              Select a conversation from the sidebar to start messaging, or create a new conversation.
            </p>
            <Button 
              onClick={() => setIsNewChatOpen(true)}
              className="bg-flux-cyan hover:bg-flux-cyan/90 text-black"
              type="button"
            >
              Start a New Conversation
            </Button>
          </div>
        </div>
      )}

      <Dialog open={isNewChatOpen} onOpenChange={setIsNewChatOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Start a New Conversation</DialogTitle>
            <DialogDescription>
              Enter a username or select from your followers below.
            </DialogDescription>
          </DialogHeader>
          
          <Command className="rounded-lg border shadow-md">
            <CommandInput 
              placeholder="Search username..." 
              value={usernameInput}
              onValueChange={setUsernameInput}
              disabled={isCreatingChat}
            />
            <CommandList>
              <CommandEmpty>No followers found</CommandEmpty>
              <CommandGroup heading="Your followers">
                {isLoadingFollowers ? (
                  <div className="py-6 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-flux-cyan mx-auto"></div>
                    <p className="text-sm text-muted-foreground mt-2">Loading followers...</p>
                  </div>
                ) : filteredFollowers.length === 0 ? (
                  <div className="py-6 text-center text-muted-foreground">
                    {usernameInput 
                      ? `No followers match "${usernameInput}"`
                      : "You don't have any followers yet"}
                  </div>
                ) : (
                  filteredFollowers.map(follower => (
                    <CommandItem
                      key={follower.id}
                      onSelect={() => {
                        handleCreateChat(follower.id, follower.username);
                      }}
                      className="flex items-center gap-2 py-2"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={follower.avatar_url || undefined} />
                        <AvatarFallback>{follower.username[0]?.toUpperCase() || '?'}</AvatarFallback>
                      </Avatar>
                      <span>{follower.username}</span>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
          
          <div className="flex justify-between pt-4">
            <Button 
              variant="outline"
              onClick={() => {
                setIsNewChatOpen(false);
                setUsernameInput("");
              }}
              disabled={isCreatingChat}
              type="button"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleCreateChat()}
              className="bg-flux-cyan hover:bg-flux-cyan/90 text-black"
              disabled={!usernameInput.trim() || isCreatingChat}
              type="button"
            >
              {isCreatingChat ? "Creating..." : "Create Chat"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inbox;
